import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/store/app.reducer';
import { UserFile } from 'src/app/model/user-file';
import * as SiteActions from './site.actions';
import { EFileType } from 'src/app/conf/user-files';
import { TranslateService } from '@ngx-translate/core';
import { LOCALSTORAGE_CURRENT_SITE_ID } from 'src/app/conf/user';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { SiteService } from 'src/app/services/site.service';
import * as NotificationsActions from '../notifications/notifications.actions';
import * as UserFileActions from '../user-file/user-file.actions';
import { WebsocketThermoService } from 'src/app/services/websocket-thermo.service';
import { Site } from 'src/app/model/site';
import * as DeviceActions from 'src/app/store/device/device.actions';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';

@Injectable()
export class SiteEffects {

    /*on(SiteActions.setCurrentSite, (state, payload) => {
       let currentSite;
       if (state.sites.length === 0) {
           return { ...state };
       }
       const findSite = state.sites.find(site => site.Id === payload.siteId);
       if (typeof payload.siteId !== 'undefined' && findSite !== undefined && findSite !== null) {
           localStorage.setItem(LOCALSTORAGE_CURRENT_SITE_ID, payload.siteId.toString());
           currentSite = findSite;
       } else {
           currentSite = state.sites[0];
       }
       return { ...state, currentSite };
   }),*/

    setCurrentSite$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.setCurrentSite),
        withLatestFrom(
            this.store.select('site', 'sites'),
            this.store.select('deviceVct', 'sitesList'),
        ),
        switchMap(([action, sites, sitesList]) => {
            if (sites.length === 0 && sitesList.length === 0) {
                return [];
            }
            if (sites.length > 0 && sitesList.length > 0) {
                const findSite = sites.find(site => site.Id === action.siteId);
                const findSiteVct = sitesList.find(site => site.Id === action.siteId);
                if (typeof action.siteId !== 'undefined' &&
                    ((findSite !== undefined && findSite !== null) || (findSiteVct !== undefined && findSiteVct !== null))) {
                    localStorage.setItem(LOCALSTORAGE_CURRENT_SITE_ID, action.siteId.toString());
                    if (findSite !== undefined && findSite !== null) {
                        return [SiteActions.setCurrentSiteSuccess({ site: findSite })];
                    }
                    return [SiteActions.setCurrentSiteSuccess({ site: findSiteVct })];
                } else {
                    return [SiteActions.setCurrentSiteSuccess({ site: sites[0] })];
                }
            }
            if (sites.length > 0) {
                const findSite = sites.find(site => site.Id === action.siteId);
                if (typeof action.siteId !== 'undefined' && findSite !== undefined && findSite !== null) {
                    localStorage.setItem(LOCALSTORAGE_CURRENT_SITE_ID, action.siteId.toString());
                    return [SiteActions.setCurrentSiteSuccess({ site: findSite })];
                } else {
                    return [SiteActions.setCurrentSiteSuccess({ site: sites[0] })];
                }
            }
            if (sitesList.length > 0) {
                const findSite = sitesList.find(site => site.Id === action.siteId);
                if (typeof action.siteId !== 'undefined' && findSite !== undefined && findSite !== null) {
                    localStorage.setItem(LOCALSTORAGE_CURRENT_SITE_ID, action.siteId.toString());
                    return [SiteActions.setCurrentSiteSuccess({ site: findSite })];
                } else {
                    return [SiteActions.setCurrentSiteSuccess({ site: sitesList[0] })];
                }
            }
            return [];
        })
    ));

    getSite$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.getSite),
        withLatestFrom(
            this.store.select('user', 'currentUser'),
            this.store.select('site', 'currentSite'),
        ),
        switchMap(([action, currentUser, currentSite]) => {
            const siteId = action.siteId ? action.siteId : currentSite.Id;
            return this.siteService.getSite(siteId).pipe(
                map((resp) => {
                    if (resp.Success) {
                        const site: Site = { ...resp.Data, CurrentlyOwned: resp.Data.UserId === currentUser.Id };
                        return SiteActions.getSiteSuccess({ site });
                    } else {
                        return SiteActions.getSiteFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(SiteActions.getSiteFailed({ errorMessage: error.message }));
                })
            );
        })
    ));

    refreshSite$ = createEffect(() => this.actions$.pipe(
        ofType(DeviceActions.editDeviceSuccess),
        switchMap(() => [SiteActions.getSite({})])
    ));

    getSites$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.getSites),
        switchMap((action) => this.siteService.getSites(action.userId).pipe(
            map((resp) => {
                if (resp.Success) {
                    return SiteActions.getSitesSuccess({ sites: resp.Data });
                } else {
                    return SiteActions.getSitesFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(SiteActions.getSitesFailed({ errorMessage: error.message }));
            })
        ))
    ));

    getSitesOnlineDevices$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.getSitesOnlineDevices),
        switchMap((action) => this.siteService.getSitesOnlineDevices(action.userId).pipe(
            map((resp) => {
                if (resp.Success) {
                    return SiteActions.getSitesOnlineDevicesSuccess({ sites: resp.Data });
                } else {
                    return SiteActions.getSitesOnlineDevicesFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(SiteActions.getSitesOnlineDevicesFailed({ errorMessage: error.message }));
            })
        ))
    ));

    setOwned$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.getSitesSuccess),
        withLatestFrom(this.store.select('user', 'currentUser')),
        filter(([action, currentUser]) => !!currentUser),
        switchMap(([action, currentUser]) => [SiteActions.setOwned({ currentUserId: currentUser.Id })])
    ));

    addSite$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.addSite),
        withLatestFrom(this.store.select('user', 'currentUser')),
        switchMap(([action, currentUser]) => this.siteService.insertSite(action.site)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        const site = {
                            ...action.site,
                            Id: resp.Data.Id,
                            UserId: currentUser.Id,
                            CurrentlyOwned: true,
                        };
                        return SiteActions.addSiteSuccess({
                            site,
                            background: action.background,
                            message: this.translate.instant('SITE_INSERT_SUCCESS'),
                        });
                    } else {
                        return SiteActions.addSiteFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(SiteActions.addSiteFailed({ errorMessage: error.message }));
                })
            ))
    ));

    addSiteSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.addSiteSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'new_site_completed', params: { page_title: 'Sites' } }));
            }, 500);
        }),
    ), { dispatch: false });

    addSiteFailedAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.addSiteFailed),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'new_site_failed', params: { page_title: 'Sites' } }));
            }, 500);
        }),
    ), { dispatch: false });

    saveCurrentSite$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.addSiteSuccess),
        tap((action) => localStorage.setItem(LOCALSTORAGE_CURRENT_SITE_ID, action.site.Id.toString())),
    ), { dispatch: false });

    saveSiteImage$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.addSiteSuccess, SiteActions.updateSiteSuccess),
        switchMap((action) => {
            if (!action.background.metadata) {
                return [UserFileActions.noFileToLoad()];
            }
            const userFile: UserFile = {
                Type: EFileType.IMAGE,
                Name: action.background.metadata.name,
                SiteId: action.site.Id,
                Content: btoa(action.background.content.toString()),
                MimeType: action.background.metadata.type
            };
            return [UserFileActions.addUserFile({ userFile })];
        }),
    ));

    updateSite$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.updateSite),
        switchMap((action) => this.siteService.updateSite(action.site)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return SiteActions.updateSiteSuccess({ site: action.site, background: action.background, message: this.translate.instant('SITE_UPDATE_SUCCESS'), });
                    } else {
                        return SiteActions.updateSiteFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(SiteActions.updateSiteFailed({ errorMessage: error.message }));
                })
            ))
    ));

    deleteSite$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.deleteSite),
        switchMap((action) => this.siteService.deleteSite(action.id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        //TODO REPLACEthis.dialog.closeAll(); //TODO REPLACE
                        return SiteActions.deleteSiteSuccess({ id: action.id, message: this.translate.instant('SITE_DELETE_SUCCESS') });
                    } else {
                        return SiteActions.deleteSiteFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(SiteActions.deleteSiteFailed({ errorMessage: error.message }));
                })
            ))
    ));

    deleteSharedSite$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.deleteSharedSite),
        switchMap((action) => this.siteService.deleteSharedSite(action.id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        //this.dialog.closeAll(); TODO REPLACE
                        return SiteActions.deleteSharedSiteSuccess({ id: action.id, message: this.translate.instant('SITE_DELETE_SUCCESS') });
                    } else {
                        return SiteActions.deleteSharedSiteFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(SiteActions.deleteSharedSiteFailed({ errorMessage: error.message }));
                })
            ))
    ));

    assignTechnician$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.assignTechnician),
        switchMap((action) => this.siteService.assignTechnicianToSite(action.siteId, action.technicianId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return SiteActions.assignTechnicianSuccess({ siteId: action.siteId, technicianId: action.technicianId });
                    } else {
                        return SiteActions.assignTechnicianFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SiteActions.assignTechnicianFailed({ errorMessage: error.message })))
            )
        )
    ));

    unassignTechnician$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.unassignTechnician),
        switchMap((action) => this.siteService.unassignTechnicianToSite(action.siteId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return SiteActions.unassignTechnicianSuccess({ siteId: action.siteId });
                    } else {
                        return SiteActions.unassignTechnicianFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SiteActions.unassignTechnicianFailed({ errorMessage: error.message })))
            )
        )
    ));

    getSiteTransfers$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.getSiteTransfers),
        switchMap(() => this.siteService.getSiteTransfers()
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return SiteActions.getSiteTransfersSuccess({ siteTransfers: resp.Data });
                    } else {
                        return SiteActions.getSiteTransfersFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SiteActions.getSiteTransfersFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteSiteTransfer$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.deleteSiteTransfer),
        map(action => action.id),
        switchMap((id) => this.siteService.deleteSiteTransfer(id)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return SiteActions.deleteSiteTransferSuccess({
                            transferId: id,
                            message: this.translate.instant('SITE_TRANSFER_DELETE_SUCCESS')
                        });
                    } else {
                        return SiteActions.deleteSiteTransferFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SiteActions.deleteSiteTransferFailed({ errorMessage: error.message })))
            )
        )
    ));

    transferSite$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.transferSite),
        switchMap((action) => this.siteService.transferSiteNew(action.email, action.firstName, action.lastName, action.siteId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        let message = this.translate.instant('SITE_TRANSFER_INVITATION_SENT');
                        if (typeof resp.Data === 'string') {
                            message = this.translate.instant(resp.Data);
                        }
                        return SiteActions.transferSiteSuccess({ message });
                    } else {
                        return SiteActions.transferSiteFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => {
                    if (error.error !== undefined && error.error.ErrorMessage !== undefined && error.error.ErrorMessage === 'cannot transfer site to technician user') {
                        return of(SiteActions.transferSiteFailed({ errorMessage: this.translate.instant('SHARE_SITE_FAILED_ONLY_USER') }));
                    }
                    return of(SiteActions.transferSiteFailed({ errorMessage: error.message }));
                })
            )
        )
    ));

    confirmSiteTransfer$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.confirmSiteTransfer),
        switchMap((action) => this.siteService.confirmSiteTransfer(action.siteId, action.ownerId, action.verification, action.userId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return SiteActions.confirmSiteTransferSuccess({
                            message: this.translate.instant('SITE_TRANSFER_ACCEPTED_SUCCESS'),
                        });
                    } else {
                        if (resp.ErrorMessage.includes('sites_acl.sites_acl_site_id_user_id_unique')) {
                            return SiteActions.confirmSiteTransferFailed({ errorMessage: this.translate.instant('DUPLICATE_SITE_TRASFERT_CONFIRM') });
                        }
                        return SiteActions.confirmSiteTransferFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SiteActions.confirmSiteTransferFailed({ errorMessage: error.message })))
            )
        )
    ));

    reloadSites$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.confirmSiteTransferSuccess),
        mergeMap(() => [SiteActions.getSites({}), SiteActions.getSitesOnlineDevices({})])
    ));

    // getThermoDevice$ = createEffect(() => this.actions$.pipe(
    //     ofType(SiteActions.getThermoDevice),
    //     switchMap((action) => this.siteService.getThermoDevice(action.keycode).pipe(
    //         map((resp) => {
    //             if (resp !== undefined) {
    //                 return SiteActions.getThermoDeviceSuccess({ deviceThermo: resp });
    //             } else {
    //                 return SiteActions.getThermoDeviceFailed({ errorMessage: this.translate.instant('ERROR_GET_DATA_THERMO') });
    //             }
    //         }),
    //         catchError(error => {
    //             console.error(error);
    //             return of(SiteActions.getThermoDeviceFailed({ errorMessage: this.translate.instant('ERROR_GET_DATA_THERMO') }));
    //         })
    //     ))
    // ));


    // updateThermo$ = createEffect(() => this.actions$.pipe(
    //     ofType(SiteActions.updateThermo),
    //     switchMap((action) => this.siteService.updateThermo(action.thermo)
    //         .pipe(
    //             map(resp => {
    //                 if (resp.ok) {
    //                     return SiteActions.updateThermoSuccess({ message: this.translate.instant('SUCCESS_SET_DATA_THERMO') });
    //                 } else {
    //                     return SiteActions.updateThermoFailed({ errorMessage: this.translate.instant('ERROR_SET_DATA_THERMO') });
    //                 }
    //             }),
    //             catchError(error => {
    //                 console.error(error);
    //                 return of(SiteActions.updateThermoFailed({ errorMessage: this.translate.instant('ERROR_SET_DATA_THERMO') }));
    //             })
    //         ))
    // ));


    // openWebsocketThermo$ = createEffect(() => this.actions$.pipe(
    //     ofType(SiteActions.openWebsocketThermo),
    //     withLatestFrom(
    //         this.store.select('device', 'currentDevice'),
    //         this.store.select('auth', 'tokenData')
    //     ),
    //     tap(([action, device, tokenData]) => {
    //         this.websocketThermoService.openWebSocket(tokenData.access_token, device.Keycode, device.Keycode);
    //     })
    // ), { dispatch: false });

    // closeWebsocketThermo$ = createEffect(() => this.actions$.pipe(
    //     ofType(SiteActions.closeWebsocketThermo),
    //     tap(() => {
    //         this.websocketThermoService.closeWebSocket();
    //     })
    // ), { dispatch: false });

    updateAutomationRtc$ = createEffect(() => this.actions$.pipe(
        ofType(SiteActions.updateAutomationRtc),
        switchMap((action) => this.siteService.updateAutomationRtc(action.siteId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return SiteActions.updateAutomationRtcSuccess({ message: this.translate.instant('UPDATE_RTC_SUCCESS') });
                    } else {
                        return SiteActions.updateAutomationRtcFailed({ errorMessage: this.translate.instant('UPDATE_RTC_FALLITO_DEVICES') });
                    }
                }),
                catchError(error => {
                    console.error(error);
                    return of(SiteActions.updateAutomationRtcFailed({ errorMessage: this.translate.instant('UPDATE_RTC_FALLITO_DEVICES') }));
                })
            ))
    ));

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            SiteActions.addSiteSuccess,
            SiteActions.updateSiteSuccess,
            SiteActions.transferSiteSuccess,
            SiteActions.confirmSiteTransferSuccess,
            // SiteActions.updateThermoSuccess,
            SiteActions.deleteSiteTransferSuccess,
            SiteActions.deleteSiteSuccess,
            SiteActions.updateAutomationRtcSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("SITE", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true })); // delete 'toastOnly: true' for push notification on ringbell topbar list ( 'toastOnly: false' non funziona !! )
        })
    ), { dispatch: false });

    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            SiteActions.addSiteFailed,
            SiteActions.transferSiteFailed,
            SiteActions.confirmSiteTransferFailed,
            // SiteActions.updateThermoFailed,
            SiteActions.confirmSiteDeviceTransferShareError,
            SiteActions.updateAutomationRtcFailed,
            SiteActions.deleteSiteFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("SITE", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private siteService: SiteService,
        private translate: TranslateService,
        private websocketThermoService: WebsocketThermoService,
    ) { }
}
